import {
  Component,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnInit,
  OnDestroy,
} from '@angular/core';
import * as THREE from 'three';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { MTLLoader } from 'three/examples/jsm/loaders/MTLLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { fabric } from 'fabric';
import { ActivatedRoute, Router } from '@angular/router';
import { CarritoService } from 'src/app/services/carrito.service';
import { FirestoreService } from 'src/app/services/firestore.service';
import { Subscription } from 'rxjs';
import { getStorage, ref, uploadString, getDownloadURL } from 'firebase/storage';
import { AuthService } from 'src/app/services/auth.service';
import { AlertController, ModalController } from '@ionic/angular';
import { ModalTallaCantidadComponent } from 'src/app/components/modal-talla-cantidad/modal-talla-cantidad.component';

// ─── Tipos ────────────────────────────────────────────────────

type ColorType = 'solid' | 'acid';
type Vista     = 'frente' | 'espalda' | 'lateral';

interface ColorItem {
  color:   string;
  type:    ColorType;
  display: string;
}

// Posiciones de cámara para cada vista
const CAMERA_POSITIONS: Record<Vista, THREE.Vector3> = {
  frente:  new THREE.Vector3(0, 30,  100),
  espalda: new THREE.Vector3(0, 30, -100),
  lateral: new THREE.Vector3(-100, 30, 0),
};

const EDITOR_SIZE  = 420;
const PREVIEW_SIZE = 1024;

// ─── Componente ───────────────────────────────────────────────

@Component({
  selector:    'app-camiseta3d',
  templateUrl: './camiseta3d.component.html',
  styleUrls:   ['./camiseta3d.component.scss'],
})
export class Camiseta3dComponent implements OnInit, AfterViewInit, OnDestroy {

  // ── ViewChild refs ──────────────────────────────────────────

  @ViewChild('threejsContainer', { static: true }) threejsContainer!: ElementRef;
  @ViewChild('previewCanvas',    { static: true }) previewCanvas!: ElementRef;
  @ViewChild('editorCanvas',     { static: true }) editorCanvas!: ElementRef;

  // ── Estado público ──────────────────────────────────────────

  product: any        = { name: '' };
  loading             = true;
  color: string       = 'white';
  colors: ColorItem[] = [];
  vistaActiva: Vista  = 'frente';
  tieneEstampado      = false;
  mostrarHint         = true;

  editorAbierto = false;
  modoEditor: 'imagen' | 'texto' | 'editar' = 'imagen';
  textoSeleccionado = false;
  textoColor  = '#ffffff';
  textoFuente = 'Impact';

  readonly googleFonts: string[] = [
    'Impact',
    'Bebas Neue',
    'Anton',
    'Oswald',
    'Teko',
    'Alfa Slab One',
    'Black Han Sans',
    'Righteous',
    'Fredoka One',
    'Boogaloo',
    'Pacifico',
    'Lobster',
    'Abril Fatface',
    'Dancing Script',
    'Permanent Marker',
    'Satisfy',
    'Great Vibes',
    'Raleway',
    'Nunito',
    'Poppins',
  ];

  garmentOverlayUrl: string = '';

  // ── Estado privado ──────────────────────────────────────────

  private productId: string = '';
  private mtlUrl:    string = '';
  private objUrl:    string = '';

  private scene!:    THREE.Scene;
  private camera!:   THREE.PerspectiveCamera;
  private renderer!: THREE.WebGLRenderer;
  private model!:    THREE.Object3D | null;
  private controls!: OrbitControls;

  private fabricCanvas!: fabric.Canvas;
  private selectedImage: fabric.Image | null      = null;
  private fabricOverlayImage: fabric.Image | null = null;
  private autoDeselectTimer: any                  = null;
  private interactuandoConControles               = false;
  private lastAddedObject: fabric.Object | null   = null;

  private textureCache              = new Map<string, THREE.CanvasTexture>();
  private hasStoredOriginalRotation = false;
  private originalRotation          = new THREE.Euler();

  private routeSub!:       Subscription;
  private queryParamsSub!: Subscription;
  private productSub!:     Subscription;

  // ─────────────────────────────────────────────────────────────

  constructor(
    private router:          Router,
    private route:           ActivatedRoute,
    private carrito:         CarritoService,
    private firestore:       FirestoreService,
    private modalController: ModalController,
    private alertController: AlertController,
    private authService:     AuthService,
  ) {}

  // ── Lifecycle ────────────────────────────────────────────────

  async ngOnInit(): Promise<void> {
    this.routeSub = this.route.params.subscribe(params => {
      this.productId = params['id'];
      this.getProduct(this.productId);
    });

    this.queryParamsSub = this.route.queryParams.subscribe(params => {
      this.mtlUrl = params['mtlUrl'];
      this.objUrl = params['objUrl'];
    });
  }

  ngAfterViewInit(): void {
    this.initThree();
    this.initFabric();
    // El cambiarColor inicial lo gestiona getProduct una vez cargado el producto
    setTimeout(() => { this.mostrarHint = false; }, 4000);
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    this.queryParamsSub?.unsubscribe();
    this.productSub?.unsubscribe();
    clearTimeout(this.autoDeselectTimer);
    if (this.renderer) { this.renderer.dispose(); }
  }

  // ── Datos del producto ───────────────────────────────────────

  getProduct(id: string): void {
    this.productSub = this.firestore
      .getDoc('productos/', id)
      .subscribe((producto: any) => {
        this.product = producto;

        // Detectar si el producto es acid wash por su nombre
        const esAcid = (producto.name as string)
          .toLowerCase()
          .includes('acid');

        this.colors = this.convertColorsArray(
          producto.availableColors ?? [],
          esAcid,
        );

        // Aplicar el primer color con el tipo correcto al cargar
        const primerColor = this.colors[0];
        if (primerColor && this.model) {
          this.cambiarColor(primerColor.color, primerColor.type);
        }

        const moldeUrl = producto.imagenMolde ?? '';
        if (moldeUrl) {
          this.garmentOverlayUrl = moldeUrl;
          if (this.fabricCanvas) {
            this.loadGarmentOverlay(moldeUrl);
          }
        }
      });
  }

  convertColorsArray(rawColors: string[], esAcid: boolean = false): ColorItem[] {
    return rawColors.map(c => ({
      color:   c,
      type:    esAcid ? 'acid' as ColorType : 'solid' as ColorType,
      display: c,
    }));
  }

  // ── Utilidad: luminosidad ────────────────────────────────────

  private isColorDark(color: string): boolean {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 1;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, 1, 1);
    const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
    return (0.299 * r + 0.587 * g + 0.114 * b) < 128;
  }

  // ── Cambio de color ──────────────────────────────────────────

  cambiarColor(color: string, type: ColorType = 'solid'): void {
    this.color = color;

    if (this.fabricCanvas) {
      this.fabricCanvas.setBackgroundColor(
        color,
        this.fabricCanvas.renderAll.bind(this.fabricCanvas),
      );
    }

    if (this.renderer) {
      this.renderer.setClearColor(
        this.isColorDark(color) ? 0x4fc3f7 : 0x0a1628, 1
      );
    }

    if (!this.model) { return; }

    if (type === 'acid') {
      // Limpiar cache para regenerar con nueva semilla aleatoria cada vez
      this.textureCache.delete(`acid-${color}`);

      this.model.traverse((child: any) => {
        if (!child.isMesh) { return; }
        child.material = new THREE.MeshStandardMaterial({
          map:       this.getAcidTexture(color),
          roughness: 0.85,
          metalness: 0.0,
        });
        child.material.needsUpdate = true;
      });

      if (this.tieneEstampado) { this.updateTextureInRealTime(); }

    } else {
      // Solid: color puro sin textura procedural
      const threeColor = new THREE.Color(color);
      this.model.traverse((child: any) => {
        if (!child.isMesh) { return; }
        child.material = new THREE.MeshStandardMaterial({
          color:     threeColor,
          roughness: 0.85,
          metalness: 0.0,
        });
        child.material.needsUpdate = true;
      });

      // Si tiene estampado encima, aplicarlo sobre el color sólido
      if (this.tieneEstampado) { this.updateTextureInRealTime(); }
    }
  }

  // ── Acid wash ────────────────────────────────────────────────

private getAcidTexture(baseColor: string): THREE.CanvasTexture {
  const cacheKey = `acid-${baseColor}`;
  if (this.textureCache.has(cacheKey)) {
    return this.textureCache.get(cacheKey)!;
  }

  const size = 512;
  const off  = document.createElement('canvas');
  off.width  = size;
  off.height = size;
  const ctx  = off.getContext('2d')!;

  // ── Detectar luminancia del color base ─────────────────────
  const tmpC = document.createElement('canvas');
  tmpC.width = tmpC.height = 1;
  const tmpX = tmpC.getContext('2d')!;
  tmpX.fillStyle = baseColor;
  tmpX.fillRect(0, 0, 1, 1);
  const [br, bg, bb] = tmpX.getImageData(0, 0, 1, 1).data;
  const luminancia = 0.299 * br + 0.587 * bg + 0.114 * bb;
  const esClaro    = luminancia > 160;

  // Color de contraste según luminancia
  const colorContraste     = esClaro ? '0,0,0'         : '255,255,255';
  const colorContrasteInv  = esClaro ? '255,255,255'   : '0,0,0';

  // ── 1. Base del color ────────────────────────────────────
  ctx.fillStyle = baseColor;
  ctx.fillRect(0, 0, size, size);

  // ── 2. Parches orgánicos medianos ────────────────────────
  for (let i = 0; i < 45; i++) {
    const cx    = Math.random() * size;
    const cy    = Math.random() * size;
    const rx    = Math.random() * .5 + .05;
    const ry    = Math.random() * .3 + .07;
    const angle = (Math.random() * 60 - 30) * (Math.PI / 180);
    // En claros: solo manchas oscuras. En oscuros: mezcla.
    const usarContraste = esClaro ? true : Math.random() > 0.45;
    const color = usarContraste ? colorContraste : colorContrasteInv;

const alpha = esClaro
  ? Math.random() *  1 + 1
  : Math.random() * 1 + 1
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angle);

    const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, Math.max(rx, ry));
    grad.addColorStop(0,   `rgba(${color},${alpha})`);
    grad.addColorStop(0.5, `rgba(${color},${alpha * 0.35})`);
    grad.addColorStop(1,   `rgba(${color},0)`);

    ctx.scale(rx / Math.max(rx, ry), ry / Math.max(rx, ry));
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(0, 0, Math.max(rx, ry), 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // ── 3. Salpicaduras pequeñas ─────────────────────────────
  for (let i = 0; i < 60; i++) {
    const cx    = Math.random() * size;
    const cy    = Math.random() * size;
    const r     = Math.random() * .3 + .1;
    const usarContraste = esClaro ? true : Math.random() > 0.4;
    const color = usarContraste ? colorContraste : colorContrasteInv;
    const alpha = esClaro
      ? Math.random() * 1 + 0.18
      : Math.random() * 1 + 0.14;

    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    grad.addColorStop(0,   `rgba(${color},${alpha})`);
    grad.addColorStop(0.5, `rgba(${color},${alpha * 0.3})`);
    grad.addColorStop(1,   `rgba(${color},0)`);

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
  }

  // ── 4. Micro-salpicaduras (puntitos finos) ───────────────
  for (let i = 0; i < 80; i++) {
    const cx    = Math.random() * size;
    const cy    = Math.random() * size;
    const r     = Math.random() * 1 + 0.5;
    const usarContraste = esClaro ? true : Math.random() > 0.3;
    const color = usarContraste ? colorContraste : colorContrasteInv;
    const alpha = esClaro
      ? Math.random() * 1 + 1
      : Math.random() * 1 + .5;

    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    grad.addColorStop(0,   `rgba(${color},${alpha})`);
    grad.addColorStop(0.5, `rgba(${color},${alpha * 0.3})`);
    grad.addColorStop(1,   `rgba(${color},0)`);

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
  }

  // ── 5. Ruido granulado fino ──────────────────────────────
  ctx.globalAlpha = 1;
  const colorRuido = esClaro ? '#000000' : '#ffffff';
  for (let i = 0; i < 8000; i++) {
    ctx.globalAlpha = Math.random() * (esClaro ? 0.29 : 0.25);
    ctx.fillStyle   = colorRuido;
    ctx.fillRect(
      Math.random() * size,
      Math.random() * size,
      Math.random() * 1.2 + 0.4,
      Math.random() * 1.2 + 0.4,
    );
  }
  ctx.globalAlpha = 1;

  const texture       = new THREE.CanvasTexture(off);
  texture.needsUpdate = true;
  this.textureCache.set(cacheKey, texture);
  return texture;
}

  // ── Three.js ─────────────────────────────────────────────────

  private initThree(): void {
    const container = this.threejsContainer.nativeElement as HTMLElement;

    this.scene = new THREE.Scene();

    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(
      container.offsetWidth  || window.innerWidth,
      container.offsetHeight || window.innerHeight,
    );
    this.renderer.setClearColor(0x080812, 1);
    this.renderer.shadowMap.enabled = true;
    container.appendChild(this.renderer.domElement);

    this.camera = new THREE.PerspectiveCamera(
      60,
      (container.offsetWidth  || window.innerWidth) /
      (container.offsetHeight || window.innerHeight),
      0.1,
      1000,
    );
    this.camera.position.copy(CAMERA_POSITIONS.frente);
    this.camera.lookAt(0, 0, 0);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.minDistance   = 40;
    this.controls.maxDistance   = 160;
    this.controls.enablePan     = false;

    const ambient = new THREE.AmbientLight(0xffffff, 0.9);
    this.scene.add(ambient);
    const key = new THREE.DirectionalLight(0xffffff, 1.4);
    key.position.set(3, 5, 5);
    this.scene.add(key);
    const fill = new THREE.DirectionalLight(0xaaaaff, 0.5);
    fill.position.set(-3, 2, -3);
    this.scene.add(fill);
    const rim = new THREE.DirectionalLight(0xffffff, 0.4);
    rim.position.set(0, -3, -5);
    this.scene.add(rim);

    const mtlLoader = new MTLLoader();
    const objLoader  = new OBJLoader();

    mtlLoader.load(this.mtlUrl, materials => {
      materials.preload();
      objLoader.setMaterials(materials);
      objLoader.load(this.objUrl, object => {
        this.model = object;
        if (!this.hasStoredOriginalRotation) {
          this.originalRotation.copy(object.rotation);
          this.hasStoredOriginalRotation = true;
        }
        this.model.position.set(0, -30, 0);
        this.scene.add(this.model);
        this.loading = false;

        // El modelo ya está listo: si el producto cargó antes, aplicar color ahora
        const primerColor = this.colors[0];
        if (primerColor) {
          this.cambiarColor(primerColor.color, primerColor.type);
        }
      });
    });

    const resizeObserver = new ResizeObserver(() => this.onResize());
    resizeObserver.observe(container);

    this.animate();
  }

  private animate(): void {
    requestAnimationFrame(() => this.animate());
    if (this.controls) { this.controls.update(); }
    this.renderer.render(this.scene, this.camera);
  }

  private onResize(): void {
    const container = this.threejsContainer.nativeElement as HTMLElement;
    const w = container.offsetWidth;
    const h = container.offsetHeight;
    if (!w || !h) { return; }
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
  }

  // ── Cambio de vista ──────────────────────────────────────────

  setVista(vista: Vista): void {
    this.vistaActiva = vista;
    if (!this.camera) { return; }

    const target    = CAMERA_POSITIONS[vista].clone();
    const duration  = 600;
    const start     = this.camera.position.clone();
    const startTime = performance.now();

    const animateCamera = (now: number) => {
      const t    = Math.min((now - startTime) / duration, 1);
      const ease = 1 - Math.pow(1 - t, 3);
      this.camera.position.lerpVectors(start, target, ease);
      this.camera.lookAt(0, 0, 0);
      this.controls.update();
      if (t < 1) { requestAnimationFrame(animateCamera); }
    };
    requestAnimationFrame(animateCamera);
  }

  // ── Fabric.js – inicialización ───────────────────────────────

  private initFabric(): void {
    this.fabricCanvas = new fabric.Canvas(this.editorCanvas.nativeElement, {
      width:           EDITOR_SIZE,
      height:          EDITOR_SIZE,
      backgroundColor: this.color,
      selection:       true,
    });

    this.fabricCanvas.on('object:moving',   () => { this.updateTextureInRealTime(); this.scheduleAutoDeselect(); });
    this.fabricCanvas.on('object:scaling',  () => { this.updateTextureInRealTime(); this.scheduleAutoDeselect(); });
    this.fabricCanvas.on('object:rotating', () => { this.updateTextureInRealTime(); this.scheduleAutoDeselect(); });

    this.fabricCanvas.on('selection:created', () => { this.onFabricSelection(); this.scheduleAutoDeselect(); });
    this.fabricCanvas.on('selection:updated', () => { this.onFabricSelection(); this.scheduleAutoDeselect(); });
    this.fabricCanvas.on('selection:cleared', () => {
      this.textoSeleccionado = false;
      clearTimeout(this.autoDeselectTimer);
      this.updateTextureInRealTime();
    });

    this.loadGoogleFonts();
    this.resizeFabricToContainer();
    window.addEventListener('resize', () => this.resizeFabricToContainer());

    if (this.garmentOverlayUrl) {
      this.loadGarmentOverlay(this.garmentOverlayUrl);
    }
  }

  loadGarmentOverlay(url: string): void {
    this.garmentOverlayUrl = url;

    if (!this.fabricCanvas) { return; }

    fabric.Image.fromURL(url, (img) => {
      if (!img) { return; }

      const size  = this.fabricCanvas.getWidth();
      const scale = Math.min(
        size / (img.width  ?? size),
        size / (img.height ?? size),
      );

      img.set({
        left:        size / 2,
        top:         size / 2,
        originX:     'center',
        originY:     'center',
        scaleX:      scale,
        scaleY:      scale,
        opacity:     0.45,
        selectable:  false,
        evented:     false,
        hasBorders:  false,
        hasControls: false,
      });

      this.fabricOverlayImage = img;
      (this.fabricCanvas as any).setOverlayImage(img, () => {
        this.fabricCanvas.renderAll();
      });
    }, { crossOrigin: 'anonymous' });
  }

  // ── Google Fonts ─────────────────────────────────────────────

  private loadGoogleFonts(): void {
    const systemFonts = ['Impact'];
    const toLoad      = this.googleFonts.filter(f => !systemFonts.includes(f));
    const params      = toLoad
      .map(f => `family=${f.replace(/ /g, '+')}:wght@400;700`)
      .join('&');
    const link   = document.createElement('link');
    link.rel     = 'stylesheet';
    link.href    = `https://fonts.googleapis.com/css2?${params}&display=swap`;
    document.head.appendChild(link);
  }

  // ── Selección en Fabric ──────────────────────────────────────

  private onFabricSelection(): void {
    const obj = this.fabricCanvas.getActiveObject();
    if (obj && (obj.type === 'i-text' || obj.type === 'text')) {
      this.textoSeleccionado = true;
      this.textoColor  = (obj as fabric.IText).fill as string || '#ffffff';
      this.textoFuente = (obj as fabric.IText).fontFamily || 'Impact';
    } else {
      this.textoSeleccionado = false;
    }
  }

  // ── Auto-deselección ─────────────────────────────────────────

  private scheduleAutoDeselect(): void {
    clearTimeout(this.autoDeselectTimer);
    this.autoDeselectTimer = setTimeout(() => {
      if (!this.fabricCanvas || this.interactuandoConControles) { return; }
      this.fabricCanvas.discardActiveObject();
      this.fabricCanvas.renderAll();
      this.updateTextureInRealTime();
    }, 2000);
  }

  pauseAutoDeselect(): void {
    this.interactuandoConControles = true;
    clearTimeout(this.autoDeselectTimer);
  }

  resumeAutoDeselect(): void {
    this.interactuandoConControles = false;
    this.scheduleAutoDeselect();
  }

  // ── Texto: color y fuente ────────────────────────────────────

  setTextoColor(color: string): void {
    const obj = this.fabricCanvas.getActiveObject() as fabric.IText;
    if (!obj || (obj.type !== 'i-text' && obj.type !== 'text')) { return; }
    this.textoColor = color;
    obj.set('fill', color);
    this.fabricCanvas.renderAll();
    this.updateTextureInRealTime();
  }

  setTextoFuente(fuente: string): void {
    const obj = this.fabricCanvas.getActiveObject() as fabric.IText;
    if (!obj || (obj.type !== 'i-text' && obj.type !== 'text')) { return; }
    this.textoFuente = fuente;
    obj.set('fontFamily', fuente);
    this.fabricCanvas.renderAll();
    setTimeout(() => {
      this.fabricCanvas.renderAll();
      this.updateTextureInRealTime();
    }, 300);
  }

  // ── Resize Fabric ────────────────────────────────────────────

  private resizeFabricToContainer(): void {
    if (!this.fabricCanvas) { return; }

    const wrapper = (this.editorCanvas.nativeElement as HTMLElement)
      .closest('.fabric-canvas-wrapper') as HTMLElement | null;
    if (!wrapper) { return; }

    const available = Math.min(wrapper.clientWidth, wrapper.clientHeight);
    if (!available || available < 10) { return; }

    const oldSize = this.fabricCanvas.getWidth();
    if (Math.abs(oldSize - available) < 1) { return; }

    const ratio = available / oldSize;

    this.fabricCanvas.getObjects().forEach(obj => {
      obj.set({
        left:   (obj.left  ?? 0) * ratio,
        top:    (obj.top   ?? 0) * ratio,
        scaleX: (obj.scaleX ?? 1) * ratio,
        scaleY: (obj.scaleY ?? 1) * ratio,
      });
      obj.setCoords();
    });

    this.fabricCanvas.setDimensions({ width: available, height: available });

    if (this.fabricOverlayImage) {
      const s = Math.min(
        available / (this.fabricOverlayImage.width  ?? available),
        available / (this.fabricOverlayImage.height ?? available),
      );
      this.fabricOverlayImage.set({
        left:   available / 2,
        top:    available / 2,
        scaleX: s,
        scaleY: s,
      });
    }

    this.fabricCanvas.calcOffset();
    this.fabricCanvas.renderAll();
  }

  // ── Abrir / cerrar editor ────────────────────────────────────

  abrirEditor(modo: 'imagen' | 'texto' | 'editar' = 'imagen'): void {
    this.modoEditor    = modo;
    this.editorAbierto = true;

    if (modo === 'editar') { this.lastAddedObject = null; }

    const active = this.fabricCanvas.getActiveObject();
    if (active) {
      active.hasControls = true;
      active.hasBorders  = true;
      this.fabricCanvas.renderAll();
    }

    setTimeout(() => {
      this.onResize();
      this.resizeFabricToContainer();
    }, 360);
  }

  cerrarEditor(confirm: boolean): void {
    if (!confirm && this.lastAddedObject) {
      this.fabricCanvas.remove(this.lastAddedObject);
      const restantes     = this.fabricCanvas.getObjects().filter(o => o.selectable);
      this.tieneEstampado = restantes.length > 0;
    }

    this.fabricCanvas.discardActiveObject();
    this.fabricCanvas.renderAll();

    this.lastAddedObject = null;
    this.editorAbierto   = false;

    this.updateTextureInRealTime();
    setTimeout(() => this.onResize(), 360);
  }

  // ── Herramientas del editor ──────────────────────────────────

  flipH(): void {
    const obj = this.fabricCanvas.getActiveObject();
    if (!obj) { return; }
    obj.set('flipX', !obj.flipX);
    this.fabricCanvas.renderAll();
    this.updateTextureInRealTime();
  }

  flipV(): void {
    const obj = this.fabricCanvas.getActiveObject();
    if (!obj) { return; }
    obj.set('flipY', !obj.flipY);
    this.fabricCanvas.renderAll();
    this.updateTextureInRealTime();
  }

  bringForward(): void {
    const obj = this.fabricCanvas.getActiveObject();
    if (!obj) { return; }
    this.fabricCanvas.bringForward(obj);
    this.fabricCanvas.renderAll();
    this.updateTextureInRealTime();
  }

  sendBackward(): void {
    const obj = this.fabricCanvas.getActiveObject();
    if (!obj) { return; }
    this.fabricCanvas.sendBackwards(obj);
    this.fabricCanvas.renderAll();
    this.updateTextureInRealTime();
  }

  eliminarSeleccionado(): void {
    const obj = this.fabricCanvas.getActiveObject();
    if (!obj) { return; }
    this.fabricCanvas.remove(obj);
    this.fabricCanvas.discardActiveObject();
    this.fabricCanvas.renderAll();
    const restantes     = this.fabricCanvas.getObjects().filter(o => o.selectable);
    this.tieneEstampado = restantes.length > 0;
    this.updateTextureInRealTime();
  }

  // ── Añadir imagen ────────────────────────────────────────────

  onFileSelected(event: any): void {
    const file: File = event.target.files[0];
    if (!file) { return; }

    const reader = new FileReader();
    reader.onload = (e: any) => {
      fabric.Image.fromURL(e.target.result, img => {
        this.selectedImage = img;

        const cs      = this.fabricCanvas?.getWidth() ?? EDITOR_SIZE;
        const maxSize = cs * 0.55;
        const scale   = Math.min(maxSize / img.width!, maxSize / img.height!);

        img.set({
          left:          cs / 2,
          top:           cs / 2,
          scaleX:        scale,
          scaleY:        scale,
          originX:       'center',
          originY:       'center',
          selectable:    true,
          hasControls:   true,
          hasBorders:    true,
          lockMovementX: false,
          lockMovementY: false,
        });

        this.fabricCanvas.add(img);
        this.fabricCanvas.setActiveObject(img);
        this.fabricCanvas.renderAll();
        this.tieneEstampado  = true;
        this.lastAddedObject = img;

        this.abrirEditor('imagen');

        const fileInput = document.getElementById('fileInput') as HTMLInputElement;
        if (fileInput) { fileInput.value = ''; }
      });
    };
    reader.readAsDataURL(file);
  }

  // ── Añadir texto ─────────────────────────────────────────────

  async abrirModalTexto(): Promise<void> {
    const alert = await this.alertController.create({
      header:  'Añadir texto',
      message: 'Escribe el texto que quieres en la camiseta',
      inputs: [
        { name: 'texto', type: 'text', placeholder: 'Tu texto aquí' },
      ],
      buttons: [
        { text: 'Cancelar', role: 'cancel' },
        {
          text: 'Añadir',
          handler: (data: { texto: string }) => {
            if (data.texto?.trim()) { this.addTextToCanvas(data.texto.trim()); }
          },
        },
      ],
    });
    await alert.present();
  }

  private addTextToCanvas(texto: string): void {
    const cs = this.fabricCanvas?.getWidth() ?? EDITOR_SIZE;

    const fabricText = new fabric.IText(texto, {
      left:        cs / 2,
      top:         cs * 0.4,
      fontSize:    cs * 0.10,
      fill:        '#ffffff',
      fontFamily:  'Impact, sans-serif',
      originX:     'center',
      originY:     'center',
      selectable:  true,
      hasControls: true,
      hasBorders:  true,
    });

    this.fabricCanvas.add(fabricText);
    this.fabricCanvas.setActiveObject(fabricText);
    this.fabricCanvas.renderAll();
    this.tieneEstampado  = true;
    this.lastAddedObject = fabricText;

    this.abrirEditor('texto');
  }

  // ── Limpiar estampado ────────────────────────────────────────

  limpiarEstampado(): void {
    this.fabricCanvas.getObjects()
      .filter(o => o.selectable)
      .forEach(o => this.fabricCanvas.remove(o));
    this.fabricCanvas.renderAll();

    this.selectedImage   = null;
    this.lastAddedObject = null;
    this.tieneEstampado  = false;

    if (this.editorAbierto) { this.editorAbierto = false; }

    // Recuperar el tipo del color activo para re-aplicar correctamente
    const colorActivo = this.colors.find(c => c.color === this.color);
    this.cambiarColor(this.color, colorActivo?.type ?? 'solid');
  }

  // ── Textura en tiempo real ───────────────────────────────────
updateTextureInRealTime(): void {
  if (!this.fabricCanvas || !this.model) { return; }

  if (this.fabricOverlayImage) {
    (this.fabricCanvas as any).overlayImage = null;
  }

  const preview = this.previewCanvas.nativeElement as HTMLCanvasElement;
  const editor  = this.editorCanvas.nativeElement  as HTMLCanvasElement;
  const ctx     = preview.getContext('2d')!;

  ctx.clearRect(0, 0, PREVIEW_SIZE, PREVIEW_SIZE);

  const colorActivo = this.colors.find(c => c.color === this.color);

  if (colorActivo?.type === 'acid') {
    // Base: textura acid
    const acidTexture = this.getAcidTexture(this.color);
    const acidCanvas  = (acidTexture as any).image as HTMLCanvasElement;
    if (acidCanvas) {
      ctx.drawImage(acidCanvas, 0, 0, PREVIEW_SIZE, PREVIEW_SIZE);
    }
  } else {
    // Base: color sólido — pintar directamente en el previewCanvas
    ctx.fillStyle = this.color;
    ctx.fillRect(0, 0, PREVIEW_SIZE, PREVIEW_SIZE);
  }

  // Encima, solo los objetos de Fabric con fondo transparente
  const bgColor = this.fabricCanvas.backgroundColor as string;
  this.fabricCanvas.backgroundColor = 'transparent' as any;
  this.fabricCanvas.renderAll();

  ctx.drawImage(editor, 0, 0, PREVIEW_SIZE, PREVIEW_SIZE);

  // Restaurar background de Fabric
  this.fabricCanvas.backgroundColor = bgColor;
  this.fabricCanvas.renderAll();

  // Restaurar overlay
  if (this.fabricOverlayImage) {
    (this.fabricCanvas as any).overlayImage = this.fabricOverlayImage;
    this.fabricCanvas.renderAll();
  }

  const texture       = new THREE.CanvasTexture(preview);
  texture.needsUpdate = true;

  this.model.traverse((child: any) => {
    if (!child.isMesh) { return; }
    child.material = new THREE.MeshStandardMaterial({
      map:       texture,
      roughness: 0.8,
      metalness: 0.05,
    });
    child.material.needsUpdate = true;
  });
}

  // ── Captura de vistas ────────────────────────────────────────

  async captureModelViews(): Promise<Record<string, string>> {
    const views: Record<string, string> = {};
    const vistas: [string, THREE.Vector3][] = Object.entries(CAMERA_POSITIONS) as any;

    for (const [key, position] of vistas) {
      this.camera.position.copy(position);
      this.camera.lookAt(0, 0, 0);
      this.controls.update();
      this.renderer.render(this.scene, this.camera);
      views[key] = this.renderer.domElement.toDataURL('image/png');
      await new Promise(r => setTimeout(r, 300));
    }

    return views;
  }

  getUserImagesFromCanvas(): string[] {
    const images: string[] = [];

    this.fabricCanvas.getObjects().forEach(obj => {
      if (obj.type === 'image' && obj.selectable) {
        const img   = obj as fabric.Image;
        const scale = 3;
        const tmp   = document.createElement('canvas');
        tmp.width   = img.getScaledWidth()  * scale;
        tmp.height  = img.getScaledHeight() * scale;
        const ctx   = tmp.getContext('2d')!;
        ctx.drawImage(img.getElement() as HTMLImageElement, 0, 0, tmp.width, tmp.height);
        images.push(tmp.toDataURL('image/png'));
      }
    });

    return images;
  }

  // ── Comprar ──────────────────────────────────────────────────

  async comprar(): Promise<void> {
    this.loading = true;

    const estampados = this.getUserImagesFromCanvas().filter(Boolean);
    const vistas     = await this.captureModelViews();

    localStorage.setItem('camisetaDiseñada', JSON.stringify({
      estampados,
      vistas,
      colorSeleccionado: this.color,
      fecha: new Date().toISOString(),
    }));

    const modal = await this.modalController.create({
      component: ModalTallaCantidadComponent,
      componentProps: {
        product:           this.product,
        tallas:            this.product?.sizes ?? [],
        colorSeleccionado: this.color,
      },
    });

    await modal.present();

    const { data } = await modal.onDidDismiss();
    if (data) {
      const { talla, cantidad } = data;
      console.log('Talla:', talla, 'Cantidad:', cantidad);
    }

    this.loading = false;
  }

  // ── Firebase Storage ─────────────────────────────────────────

  async uploadBase64Image(base64: string, path: string): Promise<string> {
    const storage  = getStorage();
    const imageRef = ref(storage, path);
    await uploadString(imageRef, base64, 'data_url');
    return getDownloadURL(imageRef);
  }
}
